<template>
  <section class="admin-users">
    <div class="container">
      <div class="panel panel-default">
        <div class="panel-heading clearfix">
          <h3 class="panel-title pull-left">User Management</h3>
          <button class="btn btn-link btn-xs pull-right" type="button" @click="startCreate" :disabled="saving">
            {{ form.admin_id ? 'New user' : 'Reset' }}
          </button>
        </div>
        <div class="panel-body">
          <form class="row" @submit.prevent="handleSubmit">
            <div class="col-sm-3 form-group">
              <label>Username</label>
              <input v-model.trim="form.username" class="form-control" required maxlength="50" />
            </div>
            <div class="col-sm-3 form-group">
              <label>Email</label>
              <input v-model.trim="form.email" class="form-control" type="email" placeholder="optional" />
            </div>
            <div class="col-sm-3 form-group">
              <label>{{ form.admin_id ? 'New Password' : 'Password' }}</label>
              <input
                v-model="form.password"
                class="form-control"
                type="password"
                :required="!form.admin_id"
                minlength="8"
                autocomplete="new-password"
              />
            </div>
            <div class="col-sm-2 form-group">
              <label>Role</label>
              <select
                v-model="form.role"
                class="form-control"
                :disabled="!auth.isSuperAdmin"
              >
                <option value="admin">Admin</option>
                <option value="super_admin">Super Admin</option>
              </select>
            </div>
            <div class="col-sm-1 form-group">
              <label>Status</label>
              <select v-model="form.is_active" class="form-control">
                <option :value="true">On</option>
                <option :value="false">Off</option>
              </select>
            </div>
            <div class="col-sm-12 text-right">
              <button type="submit" class="btn btn-danger" :disabled="saving">
                <span v-if="saving" class="glyphicon glyphicon-refresh spinning"></span>
                {{ form.admin_id ? 'Update User' : 'Create User' }}
              </button>
            </div>
          </form>
          <p v-if="formError" class="text-danger">{{ formError }}</p>
        </div>
      </div>

      <div class="panel panel-default">
        <div class="panel-heading">
          <h3 class="panel-title">Existing Users</h3>
        </div>
        <div class="panel-body table-responsive">
          <div v-if="loading" class="text-muted">Loading…</div>
          <table v-else class="table table-striped table-hover">
            <thead>
              <tr>
                <th>Username</th>
                <th>Email</th>
                <th>Role</th>
                <th>MFA</th>
                <th>Active</th>
                <th>Last Login</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="user in users" :key="user.admin_id">
                <td>{{ user.username }}</td>
                <td>{{ user.email || '—' }}</td>
                <td><span class="label label-default">{{ formatRole(user.role) }}</span></td>
                <td>
                  <span :class="['label', user.mfa_enabled ? 'label-success' : 'label-warning']">
                    {{ user.mfa_enabled ? 'Enabled' : 'Disabled' }}
                  </span>
                </td>
                <td>
                  <span :class="['label', user.is_active ? 'label-success' : 'label-default']">
                    {{ user.is_active ? 'Active' : 'Hidden' }}
                  </span>
                </td>
                <td>{{ formatDate(user.last_login) }}</td>
                <td class="actions">
                  <button class="btn btn-link btn-xs" @click="startEdit(user)">Edit</button>
                  <button class="btn btn-link btn-xs" @click="beginMfaSetup(user)" :disabled="mfa.loading">
                    {{ user.mfa_enabled ? 'Reset MFA' : 'Setup MFA' }}
                  </button>
                  <button
                    v-if="user.mfa_enabled"
                    class="btn btn-link btn-xs"
                    @click="disableMfaFor(user)"
                    :disabled="mfa.loading"
                  >
                    Disable MFA
                  </button>
                  <button
                    class="btn btn-link btn-xs text-danger"
                    @click="removeUser(user)"
                    :disabled="!canDelete(user) || deleting === user.admin_id"
                  >
                    Delete
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div v-if="mfa.secret" class="panel panel-info">
        <div class="panel-heading">
          <h3 class="panel-title">MFA Setup for {{ mfa.user?.username }}</h3>
        </div>
        <div class="panel-body row">
          <div class="col-sm-4 text-center">
            <img :src="qrCode" alt="MFA QR" class="img-responsive center-block" />
            <small class="text-muted">Scan this QR with Google Authenticator</small>
          </div>
          <div class="col-sm-8">
            <p><strong>Secret:</strong> {{ mfa.secret }}</p>
            <div class="form-inline">
              <label class="sr-only">Code</label>
              <input
                v-model="mfa.code"
                class="form-control"
                placeholder="Enter 6-digit code"
                inputmode="numeric"
              />
              <button class="btn btn-primary" @click="activateMfa" :disabled="mfa.activating">
                Verify & Enable
              </button>
              <button class="btn btn-default" @click="cancelMfa">Cancel</button>
            </div>
            <p v-if="mfa.error" class="text-danger">{{ mfa.error }}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref } from 'vue'
import { useAuthStore } from '@/store/auth'
import {
  fetchAdminUsers,
  createAdminUser,
  updateAdminUser,
  deleteAdminUser,
  setupMfa,
  activateMfa as activateMfaRequest,
  disableMfa,
  type AdminUser,
} from '@/services/adminUsers'

const auth = useAuthStore()
const users = ref<AdminUser[]>([])
const loading = ref(true)
const saving = ref(false)
const deleting = ref<number | null>(null)
const formError = ref('')

const emptyForm = () => ({
  admin_id: null as number | null,
  username: '',
  email: '',
  password: '',
  role: 'admin' as 'admin' | 'super_admin',
  is_active: true,
})
const form = reactive(emptyForm())

const mfa = reactive({
  user: null as AdminUser | null,
  secret: '',
  provisioning_uri: '',
  code: '',
  error: '',
  loading: false,
  activating: false,
})

const qrCode = computed(() => {
  if (!mfa.provisioning_uri) return ''
  return `https://chart.googleapis.com/chart?chs=200x200&cht=qr&chl=${encodeURIComponent(mfa.provisioning_uri)}`
})

onMounted(() => {
  if (!auth.isAdminRole) return
  loadUsers()
})

async function loadUsers() {
  loading.value = true
  try {
    users.value = await fetchAdminUsers()
  } finally {
    loading.value = false
  }
}

function startCreate() {
  Object.assign(form, emptyForm())
  form.role = auth.isSuperAdmin ? 'admin' : 'admin'
  formError.value = ''
}

function startEdit(user: AdminUser) {
  form.admin_id = user.admin_id
  form.username = user.username
  form.email = user.email || ''
  form.role = user.role
  form.is_active = user.is_active
  form.password = ''
  formError.value = ''
}

function canDelete(user: AdminUser) {
  if (user.admin_id === auth.adminId) return false
  if (user.role === 'super_admin' && !auth.isSuperAdmin) return false
  return true
}

async function handleSubmit() {
  if (!auth.isAdminRole) return
  formError.value = ''
  saving.value = true
  try {
    if (form.admin_id) {
      const payload: Record<string, unknown> = {
        username: form.username,
        email: form.email || null,
        role: form.role,
        is_active: form.is_active,
      }
      if (form.password) payload.password = form.password
      await updateAdminUser(form.admin_id, payload)
    } else {
      await createAdminUser({
        username: form.username,
        email: form.email || undefined,
        password: form.password,
        role: form.role,
      })
    }
    startCreate()
    await loadUsers()
  } catch (err: any) {
    formError.value = err?.response?.data?.detail || 'Unable to save user'
  } finally {
    saving.value = false
  }
}

async function removeUser(user: AdminUser) {
  if (!canDelete(user)) return
  if (!window.confirm(`Delete user ${user.username}?`)) return
  deleting.value = user.admin_id
  try {
    await deleteAdminUser(user.admin_id)
    await loadUsers()
  } catch (err: any) {
    alert(err?.response?.data?.detail || 'Failed to delete user')
  } finally {
    deleting.value = null
  }
}

async function beginMfaSetup(user: AdminUser) {
  if (!auth.isAdminRole) return
  cancelMfa()
  mfa.loading = true
  mfa.error = ''
  try {
    const data = await setupMfa(user.admin_id)
    mfa.user = user
    mfa.secret = data.secret
    mfa.provisioning_uri = data.provisioning_uri
    mfa.code = ''
  } catch (err: any) {
    const detail = err?.response?.data?.detail || 'Failed to start MFA setup'
    alert(detail)
  } finally {
    mfa.loading = false
  }
}

async function activateMfa() {
  if (!mfa.user) return
  mfa.error = ''
  mfa.activating = true
  try {
    await activateMfaRequest(mfa.user.admin_id, mfa.code)
    await loadUsers()
    cancelMfa()
  } catch (err: any) {
    mfa.error = err?.response?.data?.detail || 'Invalid code'
  } finally {
    mfa.activating = false
  }
}

function cancelMfa() {
  mfa.user = null
  mfa.secret = ''
  mfa.provisioning_uri = ''
  mfa.code = ''
  mfa.error = ''
}

async function disableMfaFor(user: AdminUser) {
  if (!auth.isAdminRole) return
  if (!window.confirm(`Disable MFA for ${user.username}?`)) return
  try {
    if (mfa.user && mfa.user.admin_id === user.admin_id) {
      cancelMfa()
    }
    await disableMfa(user.admin_id)
    await loadUsers()
  } catch (err: any) {
    alert(err?.response?.data?.detail || 'Unable to disable MFA')
  }
}

function formatDate(value?: string | null) {
  if (!value) return '—'
  return new Date(value).toLocaleString('ar-EG')
}

function formatRole(role: string) {
  return role === 'super_admin' ? 'Super Admin' : 'Admin'
}
</script>

<style scoped>
.admin-users {
  padding: 40px 0;
}
.actions button {
  margin-right: 4px;
}
.spinning {
  animation: spin 1s linear infinite;
  display: inline-block;
}
@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}
</style>
